import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { AllowSort } from '../../../core/service/factory/allow-sort';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { invoiceCommand } from '../../command/unpaid-invoice';
import { Invoice } from '../../model/invoice';
import { UnpaidInvoiceCommandHandlerResolver } from '../../service/command/unpaid-invoice-command-handler-resolver.service';
import { InvoiceService } from '../../service/data/invoice.service';
import { InvoiceDeliveryComponent } from '../invoice-delivery/invoice-delivery.component';
import { InvoiceStatusComponent } from '../invoice-status/invoice-status.component';

export abstract class InvoiceListComponentBase extends PagedObjectManagerComponent<Invoice> {
  constructor(
    private readonly _factory: ColumnDefinitionFactory,
    private readonly _accountService: UserAccountService,
    dataService: InvoiceService,
    dispatcher: CommandDispatcher,
    handlerResolver: UnpaidInvoiceCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    pagedDataService?: DataService<Invoice>
  ) {
    super(dataService, messageService, navigationService, dispatcher, handlerResolver, pagedDataService);
    this.columnDefinition = this._getColumnDefinitionGroup();
    this.contextMenuDefinition = [
      new MenuItemDefinition('INVOICE.COMMAND.GETPDF', invoiceCommand.getpdf),
      new MenuItemDefinition('INVOICE.COMMAND.GETPDF_WITH_REGULATIONS', invoiceCommand.getpdfWithRegulations),
    ];
    this.menuDefinition = [
      new MenuItemDefinition('INVOICE.COMMAND.EXPORT_WITH_INVOICE_ROWS', invoiceCommand.exportWithInvoiceRows),
    ];

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices)) {
            this.contextMenuDefinition.push(
              new MenuItemDefinition('INVOICE.COMMAND.SEND', invoiceCommand.send),
              new MenuItemDefinition('INVOICE.COMMAND.REMINDER', invoiceCommand.createReminder),
              new MenuItemDefinition('INVOICE.COMMAND.REGISTER_PAYMENT', invoiceCommand.registerPayment),
              new MenuItemDefinition('INVOICE.COMMAND.REGULATE_FROM_BALANCE', invoiceCommand.regulateFromBalance),
              new MenuItemDefinition('INVOICE.COMMAND.REGISTER_CUSTOMER_LOSS', invoiceCommand.customerLoss)
            );
            this.menuDefinition.push(
              new MenuItemDefinition('INVOICE.COMMAND.ORDER_PDF', invoiceCommand.getpdf),
              new MenuItemDefinition('INVOICE.COMMAND.SEND', invoiceCommand.send),
              new MenuItemDefinition('INVOICE.COMMAND.REMINDER', invoiceCommand.createReminder),
              new MenuItemDefinition('DELETE_BTN', invoiceCommand.deleteMany)
            );
          }

          if (account.hasAccess(ModuleNames.Invoicing, Features.AttestInvoices)) {
            this.contextMenuDefinition.splice(1, 0, new MenuItemDefinition('INVOICE.COMMAND.ATTEST', invoiceCommand.attest));
            this.menuDefinition.splice(1, 0, new MenuItemDefinition('INVOICE.COMMAND.ATTEST', invoiceCommand.attest));
          }

          if (account.hasAccess(ModuleNames.Invoicing, Features.CreditInvoices)) {
            this.contextMenuDefinition.push(new MenuItemDefinition('INVOICE.COMMAND.CREDIT', invoiceCommand.credit));
            this.menuDefinition.push(new MenuItemDefinition('INVOICE.COMMAND.CREDIT_ALL', invoiceCommand.creditAll));
          }

          if (account.hasAccess(ModuleNames.Invoicing, Features.SendForDebtCollection)) {
            this.contextMenuDefinition.splice(
              4,
              0,
              new MenuItemDefinition('INVOICE.COMMAND.DEBT_COLLECTION', invoiceCommand.debtCollection)
            );
            this.menuDefinition.splice(4, 0, new MenuItemDefinition('INVOICE.COMMAND.DEBT_COLLECTION_MANY', invoiceCommand.debtCollection));
          }

          if (account.hasAccess(ModuleNames.Invoicing, Features.GrantDeferment))
            this.contextMenuDefinition.push(new MenuItemDefinition('INVOICE.COMMAND.EDIT_DEFERMENT_DATE', invoiceCommand.setDefermentDate));
        },
      })
    );
  }

  title = 'INVOICE.HEADER';

  protected _getColumnDefinitionGroup() {
    const defaultOrder = 'Number';
    let columns = [
      this._factory.createComponentBased('INVOICE.STATUS', 'attestStatus', InvoiceStatusComponent, {
        allowSort: this,
        comparer: (a: Invoice, b: Invoice) => a.attestStatus - b.attestStatus,
        visibility: ColumnVisibility.mandatory,
      }),
      this._factory.create('INVOICE.INVOICE_NUMBER', 'invoiceNumber'),
      this._factory.create('INVOICE.OCRNUMBER', 'ocrNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('INVOICE.RECIPIENT_NAME', 'invoiceRecipient'),
      this._factory.createTranslation('INVOICE.TYPE', 'invoiceTypeText', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
        serverSideSortBinding: 'InvoiceType',
      }),
      this._factory.createDate('INVOICE.DUE_DATE', 'dueDate'),
      this._factory.createDate('INVOICE.DEFERMENT_DATE', 'defermentDate'),
      this._factory.createMoney('INVOICE.PREVIEW.AMOUNT', 'netAmount', { allowSort: this, serverSideSortBinding: defaultOrder }),
      this._factory.createMoney('INVOICE.PREVIEW.VAT', 'vatAmount', ColumnVisibility.optional),
      this._factory.createMoney('INVOICE.INVOICEAMOUNT', 'amount'),
      this._factory.createMoney('INVOICE.REMAINING_AMOUNT', 'remainingAmount', { serverSideSortBinding: 'unpaidAmount' }),
      this._factory.createDate('INVOICE.FULLY_REGULATED', 'fullyRegulated', ColumnVisibility.optional),
      this._factory.create('INVOICE.DAYS_LATE', 'paymentLateNumberOfDays', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
        serverSideSortBinding: defaultOrder,
      }),
      this._factory.createDateTime('INVOICE.LAST_REMINDER_SENT', 'lastSentReminder', {
        visibility: ColumnVisibility.optional,
        allowSort: AllowSort.No,
      }),
      this._factory.createDate('INVOICE.SENT_TO_DEBT_COLLECTION', 'sentToDebtCollection', ColumnVisibility.optional),
      this._factory.create('INVOICE.PERIOD', 'refersToPeriod', { allowSort: this, serverSideSortBinding: defaultOrder }),
      this._factory.create('COMMON.DESCRIPTION', 'invoiceDescription', { visibility: ColumnVisibility.optional, allowSort: AllowSort.No }),
      this._factory.createComponentBased('INVOICE.DELIVERED_WITH', 'deliveryMethod', InvoiceDeliveryComponent, {
        allowSort: this,
        serverSideSortBinding: defaultOrder,
        visibility: ColumnVisibility.default,
        comparer: (a: Invoice, b: Invoice) => a.deliveryMethod - b.deliveryMethod,
      }),
      this._factory.create('INVOICE.DIRECT_DEBIT_INVOICE', 'directDebitInvoice', { serverSideSortBinding: 'PaymentType' }),
      this._factory.create('COMPANY.BANK_GIRO', 'bankGiroNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.LABELING', 'labeling', ColumnVisibility.optional),
      this._factory.create('COMMON.NOTE', 'note', ColumnVisibility.optional),
      this._factory.createDate('INVOICE.CREATED', 'invoiceDate', ColumnVisibility.optional),
      this._factory.createDate('CONST.INVOICE_STATUS_ATTESTED', 'attestedDate', ColumnVisibility.optional),
      this._factory.create('INVOICE.RECIPIENT_EMAIL', 'invoiceRecipientEmail', { allowSort: this, serverSideSortBinding: defaultOrder }),
      this._factory.createTranslation('COMMON.EMAIL_DELIVERY_STATUS', 'emailDeliveryStatusText', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
        serverSideSortBinding: defaultOrder,
        helpBuilder: (inv: Invoice) => inv.emailDeliveryInfo,
      }),
    ];
    return columns;
  }
}
